import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef, useRef, useState, useLayoutEffect } from 'react';
import { Icon16ChevronDown } from '@figma/fpl-icons/icon-16-chevron-down';
import { Icon16ChevronRight } from '@figma/fpl-icons/icon-16-chevron-right';
import clsx from 'clsx';
import { Root as Root$1, CollapseContext, Label as Label$1, Trail as Trail$1, Content as Content$1, Group as Group$1 } from '../collapse-primitive/collapse-primitive.js';
import { useDetectTruncated } from '../hooks/use-detect-truncated.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { bodyLargeStrong, bodyMediumStrong } from '../styles/composables.css.js';
import { collapseRoot, fillHeight, collapseHeader, size, leftPanelVariant, fillWidth, fitWidth, collapseChevron, collapseLabel, collapseLabelTruncated, collapseLabelButton, collapseLabelButtonTruncated, collapseTrail, collapseContent, collapseGroup } from './collapse.css.js';

/**
 * Collapse.Root is an styled component that should always contain a Collapse.Header and Collapse.Content.
 *
 * @compose { "root": true, "multi": true }
 * @see https://fpl.figma.design/components/collapse/
 */
const Root = forwardRef((props, ref) => {
    return (jsx(Root$1, { "data-fpl-component": "", ...props, className: clsx(collapseRoot, {
            [fillHeight]: props.height === 'fill',
        }), ref: ref }));
});
Root.displayName = 'Collapse.Root';
/**
 * Collapse.Header is an styled component used inside a {@link Root|Collapse.Root}
 * Should always contain a {@link Label|Collapse.Label}, and can optionally contain {@link Trail|Collapse.Trail} that follows the label.
 *
 * @compose { "ancestor": ["Collapse.Root"] }
 * @see https://fpl.figma.design/components/collapse/
 */
const Header = forwardRef(({ size: size$1 = 'md', width = 'fill', variant = 'default', ...props }, ref) => {
    return (jsx("div", { "data-fpl-component": "", ...props, className: clsx(collapseHeader, size[size$1], {
            [fitWidth]: width === 'fit',
            [fillWidth]: width === 'fill',
            [leftPanelVariant]: variant === 'leftPanel',
        }), ref: ref }));
});
Header.displayName = 'Collapse.Header';
/**
 * Collapse.Label is an styled component used inside a {@link Header|Collapse.Header}.
 *
 * @compose { "ancestor": ["Collapse.Header"] }
 */
const Label = forwardRef(({ children, size = 'md', truncate, ...props }, ref) => {
    const manager = useValidatedContext(CollapseContext, 'CollapseContext', 'Collapse.Root');
    const labelRef = useRef(null);
    const isTruncated = useDetectTruncated(labelRef, !!truncate);
    const [tooltipText, setTooltipText] = useState(null);
    const tooltipProps = truncate && isTruncated && tooltipText
        ? { 'data-tooltip': tooltipText, 'data-tooltip-type': 'text' }
        : null;
    useLayoutEffect(() => {
        if (labelRef.current && truncate && isTruncated) {
            setTooltipText(labelRef.current.innerText);
        }
    });
    return (jsxs(Label$1, { "data-fpl-component": "", ...props, ref: ref, className: clsx(collapseLabelButton, {
            [collapseLabelButtonTruncated]: truncate,
        }), ...tooltipProps, children: [jsx("span", { className: collapseChevron, children: manager.isOpen ? jsx(Icon16ChevronDown, { "aria-hidden": true }) : jsx(Icon16ChevronRight, { "aria-hidden": true }) }), jsx("span", { ref: labelRef, className: clsx(collapseLabel, {
                    [bodyMediumStrong]: size === 'md',
                    [bodyLargeStrong]: size === 'lg',
                    [collapseLabelTruncated]: truncate,
                }), children: children })] }));
});
Label.displayName = 'Collapse.Label';
/**
 * Collapse.Trail is used inside a {@link Header|Collapse.Header} and appears after the collapse label.
 * Children must be type of {@link IconButton}.
 *
 * @compose { "ancestor": ["Collapse.Header"] }
 */
const Trail = forwardRef((props, ref) => {
    return (jsx(Trail$1, { "data-fpl-component": "", className: collapseTrail, ...props, ref: ref }));
});
Trail.displayName = 'Collapse.Trail';
/**
 * Collapse.Content is used inside a {@link Root|Collapse.Root}.
 *
 * @compose { "ancestor": ["Collapse.Root"] }
 * @see https://fpl.figma.design/components/collapse/
 */
const Content = forwardRef((props, ref) => {
    return (jsx(Content$1, { "data-fpl-component": "", className: collapseContent, ref: ref, ...props }));
});
Content.displayName = 'Collapse.Content';
/**
 * Collapse.Group is used to group multiple Collapses. Applies a border in between each Collapse in the group.
 * Children must be of type {@link Root|Collapse.Root}.
 *
 * @compose { "standalone": true, "strictChildren": true, "children": ["Collapse.Root"] }
 * @see https://fpl.figma.design/components/collapse/
 */
const Group = forwardRef((props, ref) => {
    return (jsx(Group$1, { "data-fpl-component": "", className: collapseGroup, ref: ref, ...props }));
});
Group.displayName = 'Collapse.Group';

export { Content, Group, Header, Label, Root, Trail };
//# sourceMappingURL=collapse.js.map
